import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";

const APP_LABEL = {
  partners: "Chandan Partners",
  formulations: "Formulation Master",
  "formulation-master": "Formulation Master",
  "dossier-assistant": "Dossier Assistant",
  dossiers: "Dossier Assistant",
  activity: "Recent activity · past 24h",
  "clinical-intelligence": "Clinical Intelligence",
};

const APP_URL = {
  partners: "/partners",
  formulations: "/formulations",
  "formulation-master": "/formulations",
  "dossier-assistant": "/dossiers",
  dossiers: "/dossiers",
  "clinical-intelligence": "/clinical-intelligence",
};

const APP_INTERNAL = {
  partners: true,
  formulations: true,
  "formulation-master": true,
  "dossier-assistant": true,
  dossiers: true,
  "clinical-intelligence": true,
};

// Human-readable label per event.source_app inside the activity bucket.
const ACTIVITY_APP_LABEL = {
  partners: "Partners",
  formulations: "Formulations",
  dossiers: "Dossiers",
};

function ageLabel(age_minutes) {
  if (age_minutes == null) return "";
  if (age_minutes < 1) return "just now";
  if (age_minutes < 60) return `${age_minutes} min ago`;
  const hrs = Math.floor(age_minutes / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function useDebounce(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function UnifiedSearch() {
  const [query, setQuery] = useState("");
  const debounced = useDebounce(query, 250);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const wrapRef = useRef(null);
  const requestId = useRef(0);
  const [error, setError] = useState("");

  const runSearch = useCallback(async (q) => {
    const request = ++requestId.current;
    setError("");
    if (!q || q.trim().length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/search", { params: { q } });
      if (request === requestId.current) setResults(data);
    } catch {
      if (request === requestId.current) { setResults(null); setError("Search could not load. Try again or open the workspace directly."); }
    } finally {
      if (request === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    runSearch(debounced);
    return () => { requestId.current += 1; };
  }, [debounced, runSearch]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const grouped = results?.grouped || {};
  const orderedSlugs = [
    "partners",
    "formulations",
    "formulation-master",
    "dossier-assistant",
    "dossiers",
    "clinical-intelligence",
    "activity",
  ];
  const hasQuery = query.trim().length >= 2;

  return (
    <div ref={wrapRef} className="relative" data-testid="unified-search">
      <div className="relative">
        <Search
          size={16}
          strokeWidth={1.75}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10"
        />
        <input
          type="text"
          aria-label="Search research and sourcing records"
          maxLength={200}
          onKeyDown={e => { if (e.key === "Escape") setOpen(false); }}
          style={{ paddingLeft: 44, paddingRight: 40 }}
          className="input"
          placeholder="Search across Partners, Formulations, Dossiers…"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            requestId.current += 1;
            setResults(null);
            setError("");
            setLoading(e.target.value.trim().length >= 2);
            setQuery(e.target.value);
            setOpen(true);
          }}
          data-testid="unified-search-input"
        />
        {query && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              requestId.current += 1;
              setLoading(false);
              setError("");
              setQuery("");
              setResults(null);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground z-10"
            data-testid="unified-search-clear"
          >
            <X size={14} />
          </button>
        )}
      </div>

      <AnimatePresence>
        {open && hasQuery && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="absolute left-0 right-0 mt-2 border border-border overflow-hidden z-50 shadow-md"
            style={{ background: "var(--vl-surface)", color: "var(--vl-ink)", borderRadius: "var(--radius)", overflowWrap: "anywhere" }}
            data-testid="unified-search-results"
          >
            <div
              className="px-4 py-2 flex items-center justify-between"
              style={{ borderBottom: "1px solid var(--vl-line)", background: "var(--vl-inset)" }}
            >
              <span className="font-mono-meta" style={{ color: "var(--vl-ink)" }}>
                Search results
              </span>
              <span className="font-mono-meta" style={{ color: "var(--vl-muted)" }}>
                {loading ? "Searching…" : `${results?.total ?? 0} matches`}
              </span>
            </div>

            <div className="max-h-[380px] overflow-y-auto">
              {error && <p role="alert" className="px-4 py-4">{error}</p>}
              {!loading && !error && results && (results.total ?? 0) === 0 && (
                <div className="px-4 py-6">
                  <p className="text-sm mb-2" style={{ color: "var(--vl-ink)" }}>
                    No results yet for “{query}”.
                  </p>
                  <p
                    className="text-xs leading-relaxed"
                    style={{ color: "var(--vl-muted)" }}
                  >
                    Try a product name, project name, supplier or country. Only records you can access are shown.
                  </p>
                </div>
              )}

              {orderedSlugs.map((slug) => {
                const items = grouped[slug] || [];
                if (items.length === 0) return null;
                const isActivity = slug === "activity";
                return (
                  <div
                    key={slug}
                    style={{ borderBottom: "1px solid var(--vl-line)" }}
                    className="last:border-0"
                  >
                    <div
                      className="flex items-center justify-between px-4 py-2"
                      style={{ background: "var(--vl-inset)" }}
                    >
                      <span
                        className="font-mono-meta flex items-center gap-2"
                        style={{ color: "var(--vl-ink)" }}
                      >
                        {isActivity && (
                          <span
                            className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"
                            aria-hidden
                          />
                        )}
                        {APP_LABEL[slug] || slug}
                      </span>
                      {!isActivity && APP_URL[slug] && (
                        <a
                          href={APP_URL[slug]}
                          {...(APP_INTERNAL[slug]
                            ? {}
                            : { target: "_blank", rel: "noopener noreferrer" })}
                          className="font-mono-meta link-underline"
                          style={{ color: "var(--vl-ink)" }}
                        >
                          Open workspace →
                        </a>
                      )}
                      {isActivity && (
                        <span
                          className="font-mono-meta"
                          style={{ color: "var(--vl-muted)" }}
                        >
                          {items.length} event{items.length === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                    <ul>
                      {items.map((it, i) => {
                        const meta = isActivity
                          ? `${ACTIVITY_APP_LABEL[it.source_app] || it.source_app || "activity"} · ${ageLabel(it.age_minutes)}`
                          : `${it.event_type} · ${it.created_at?.slice(0, 10) || ""}`;
                        const RowInner = (
                          <>
                            <div
                              className="font-serif text-[17px]"
                              style={{ color: "var(--vl-ink)" }}
                            >
                              {it.title || it.event_type}
                            </div>
                            {it.snippet && (
                              <div
                                className="text-sm mt-1 line-clamp-2"
                                style={{ color: "var(--vl-muted)" }}
                              >
                                {it.snippet}
                              </div>
                            )}
                            <div
                              className="font-mono-meta mt-1"
                              style={{ color: "var(--vl-muted)" }}
                            >
                              {meta}
                            </div>
                          </>
                        );
                        const rowStyle = {
                          borderTop: i === 0 ? "none" : "1px solid var(--vl-line)",
                        };
                        const rowHover = {
                          onMouseEnter: (e) => (e.currentTarget.style.background = "var(--vl-inset)"),
                          onMouseLeave: (e) => (e.currentTarget.style.background = "transparent"),
                        };
                        if (it.url) {
                          return (
                            <li key={i} data-testid={`search-result-${slug}-${i}`}>
                              <a
                                href={it.url}
                                className="block px-4 py-3 transition-colors cursor-pointer"
                                style={rowStyle}
                                {...rowHover}
                              >
                                {RowInner}
                              </a>
                            </li>
                          );
                        }
                        return (
                          <li
                            key={i}
                            className="px-4 py-3 transition-colors"
                            style={rowStyle}
                            {...rowHover}
                            data-testid={`search-result-${slug}-${i}`}
                          >
                            {RowInner}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
